import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Switch
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  List as ListIcon,
  Route as RouteIcon,
  Map as MapIcon,
  Logout as LogoutIcon,
  AccountCircle,
  ChevronLeft as ChevronLeftIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import EcoRenovaES from '../assets/images/EcoRenovaES.png';
import authService from '../services/authService';

const drawerWidth = 240;

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const { t, i18n } = useTranslation();
  // Vérification temporaire (à retirer ensuite)
  console.log('i18n instance:', i18n);

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);

  const user = authService.getUser();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    authService.logout();
  };

  const navigateTo = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  // Fonction pour basculer la langue
  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'es' : 'fr';
    if (typeof i18n.changeLanguage === 'function') {
      i18n.changeLanguage(newLang);
    } else {
      console.error('i18n.changeLanguage n\'est pas une fonction.', i18n);
    }
  };

  const menuItems = [
    { text: t('menu.dashboard') || 'Tableau de bord', icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('menu.calendar') || 'Calendrier', icon: <CalendarIcon />, path: '/calendar' },
    { text: t('menu.rdvList') || 'Liste des RDV', icon: <ListIcon />, path: '/rdv-list' },
    { text: t('menu.map') || 'Carte des RDV', icon: <MapIcon />, path: '/map' },
    { text: t('menu.distanceManagement') || 'Gestion des distances', icon: <RouteIcon />, path: '/distance-management' },
    { text: t('menu.timeSlotAvailability') || 'Plages horaires', icon: <ScheduleIcon />, path: '/time-slot-availability' }
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: drawerOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {/* Bouton Hamburger */}
          <IconButton
            color="inherit"
            aria-label="ouvrir le menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Box
            component="img"
            src={EcoRenovaES}
            alt="Logo EcoRenovaES"
            sx={{ height: 40, mr: 2 }}
          />

          {/* Titre */}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {t('appTitle')}
          </Typography>

          {/* Switch de langue */}
          <Switch
            checked={i18n.language === 'es'}
            onChange={toggleLanguage}
            color="default"
            inputProps={{ 'aria-label': 'switch language' }}
          />
          <Typography variant="body2" sx={{ mr: 2 }}>
            {i18n.language === 'fr' ? t('language.french') : t('language.spanish')}
          </Typography>

          {/* Profil utilisateur */}
          <Tooltip title={t('profile.accountSettings')}>
            <IconButton
              size="large"
              edge="end"
              aria-label="compte de l'utilisateur"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.username?.charAt(0) || <AccountCircle />}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {user?.username || 'Utilisateur'}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('profile.logout') || 'Déconnexion'} />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 1 }}>
            <IconButton onClick={handleDrawerToggle}>
              <ChevronLeftIcon />
            </IconButton>
          </Box>
        )}
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigateTo(item.path)}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? 'primary.main' : 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Contenu principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
          ml: 0,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
